<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { pmaSvg } from '@/util/icons'
import { threadsUrls } from '@/util/links'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import AssessmentSchemaPreview from './AssessmentSchemaPreview.vue'
import DeleteGeneratedAssessmentButton from './DeleteGeneratedAssessmentButton.vue'
import { GENERATED_ASSESSMENT_EVENTS } from './event_names'
import { focusAfterTileRemoval, tilePosition } from './focus_after_tile_removal'
import type { GeneratedAssessment } from './generated_assessment_types'

const props = withDefaults(defineProps<{
  assessment: GeneratedAssessment
  isDeletable?: boolean
}>(), {
  isDeletable: false
})

const emit = defineEmits<{
  deleted: [uuid: string]
}>()

const analytics = getAnalyticsTracker()
const tileRef = ref<HTMLElement | null>(null)
const viewUrl = computed(() => threadsUrls.assessmentViewer(props.assessment.uuid))

// Keyed to the whole tile rather than the link, so focus on the delete button, which
// sits outside the link, keeps the panel and the button revealed.
const HIDDEN_ON_REVEAL = 'group-focus-within:invisible group-hover:invisible'
const SHOWN_ON_REVEAL = 'invisible group-focus-within:visible group-hover:visible'

// saved_goal_uuid comes from the page's analytics context rather than from this tile.
function trackView() {
  analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.CLICKED_VIEW)
}

async function removeAfterDelete(wasFocusedWhenClicked: boolean) {
  const position = tileRef.value ? tilePosition(tileRef.value) : null
  emit('deleted', props.assessment.uuid)
  if (!wasFocusedWhenClicked || !position) {
    return
  }
  await nextTick()
  focusAfterTileRemoval(position)
}
</script>

<template>
  <!-- fs-exclude on the root: the title is teacher-authored goal text and also appears in
       the link's and delete button's aria-labels, which an element-level exclusion would miss. -->
  <div
    ref="tileRef"
    data-testid="generated-assessment-tile"
    class="fs-exclude group relative mb-4 mr-6 mt-3 h-[119px] w-44 border border-gray-400"
  >
    <a
      data-testid="generated-assessment-link"
      class="relative block size-full bg-white text-[inherit] no-underline hover:text-[inherit] hover:no-underline focus:text-[inherit] focus:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
      :href="viewUrl"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="props.assessment.title"
      @click="trackView"
    >
      <div class="absolute inset-x-0 bottom-8 top-0 overflow-hidden" aria-hidden="true">
        <AssessmentSchemaPreview :schema-key="props.assessment.schema_key" />
      </div>

      <span
        class="absolute right-2 top-2 z-10 rounded-full bg-gradient-to-r from-ai-bluePinkStart from-50% to-ai-bluePinkStop px-2 py-px text-sm font-bold leading-snug tracking-wide text-white"
        :class="HIDDEN_ON_REVEAL"
        aria-hidden="true"
      >AI</span>

      <div
        class="absolute bottom-0 left-0 flex h-8 w-full items-center bg-gray-300"
        :class="HIDDEN_ON_REVEAL"
        aria-hidden="true"
      >
        <span class="flex flex-none items-center p-1.5 text-gray-600" v-html="pmaSvg" />
        <span class="truncate pr-1.5 text-md font-medium">{{ props.assessment.title }}</span>
      </div>

      <div
        class="absolute inset-0 z-20 flex flex-col justify-between bg-gray-300 px-1 pb-2.5 pt-1.5 text-left"
        :class="SHOWN_ON_REVEAL"
        aria-hidden="true"
      >
        <div
          class="flex min-h-0 flex-auto gap-0.5 overflow-hidden text-md leading-snug"
          :class="{ 'pr-7': props.isDeletable }"
        >
          <span class="flex flex-none items-start py-1 pl-0.5 pr-1 text-gray-600" v-html="pmaSvg" />
          <span class="line-clamp-5">{{ props.assessment.title }}</span>
        </div>
      </div>
    </a>

    <div v-if="props.isDeletable" class="absolute right-1 top-1 z-30" :class="SHOWN_ON_REVEAL">
      <DeleteGeneratedAssessmentButton
        :assessment-uuid="props.assessment.uuid"
        :assessment-title="props.assessment.title"
        :schema-key="props.assessment.schema_key"
        @deleted="removeAfterDelete"
      />
    </div>
  </div>
</template>
